import { useEffect, useMemo, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import type { Phase, PhaseScheduleEntry, TeamConfig } from '@jw-av/content'
import { api } from '../lib/api'
import { TeamTabs } from '../components/learn/TeamTabs'
import { RoleFocusChips } from '../components/learn/RoleFocusChips'
import { PhaseTimeline } from '../components/learn/PhaseTimeline'

const VALID_CREWS = ['audio', 'video', 'stage', 'it'] as const
type Crew = typeof VALID_CREWS[number]

interface ModuleRecord {
  id: string
  title: string
  path: string
  crew: string
  teams: string[]
  roles: string[]
  phase: string
  stage: string
  slideCount: number
}

interface ProgressRecord {
  moduleId: string
  status: string
}

const CREW_TITLES: Record<Crew, { title: string; description: string }> = {
  audio: {
    title: 'Audio Crew Training',
    description: 'Learn mixing, microphones, monitor mixing, and audio system installation.',
  },
  video: {
    title: 'Video Crew Training',
    description: 'Learn camera operation, switching, media playback, and video system installation.',
  },
  stage: {
    title: 'Stage Crew Training',
    description: 'Learn participant guidance, microphone handling, furniture, and stage choreography.',
  },
  it: {
    title: 'IT Crew Training',
    description: 'IT support and streaming infrastructure. Content will be added once role definitions are confirmed.',
  },
}

export default function LearnCrewPage() {
  const { crew: crewParam } = useParams<{ crew: string }>()
  const crew: Crew | null =
    crewParam && VALID_CREWS.includes(crewParam as Crew) ? (crewParam as Crew) : null

  const [activeTeam, setActiveTeam] = useState<'operations' | 'installation'>('operations')
  const [activeRole, setActiveRole] = useState<string>('all')
  const [phases, setPhases] = useState<Phase[]>([])
  const [team, setTeam] = useState<TeamConfig | null>(null)
  const [schedule, setSchedule] = useState<PhaseScheduleEntry[]>([])
  const [modules, setModules] = useState<ModuleRecord[]>([])
  const [progress, setProgress] = useState<ProgressRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Reset role filter when team changes
  useEffect(() => {
    setActiveRole('all')
  }, [activeTeam])

  useEffect(() => {
    if (!crew) return
    const activeCrew: Crew = crew
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [phasesRes, teamRes, scheduleRes, modulesRes, progressRes] = await Promise.all([
          api.getPhases(activeCrew, activeTeam),
          api.getTeam(activeCrew, activeTeam),
          api.getPhaseSchedule(),
          api.getModules({ crew: activeCrew, team: activeTeam }),
          api.getProgress(),
        ])
        if (cancelled) return
        setPhases(phasesRes.phases)
        setTeam(teamRes.team)
        setSchedule(scheduleRes.schedule)
        setModules(modulesRes.modules)
        setProgress(progressRes.progress ?? [])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [crew, activeTeam])

  const completedIds = useMemo(
    () => new Set(progress.filter((p) => p.status === 'completed').map((p) => p.moduleId)),
    [progress],
  )

  if (!crew) return <Navigate to="/learn" replace />
  const hasTeams = crew !== 'stage' && crew !== 'it'
  const meta = CREW_TITLES[crew]

  return (
    <div className="crew-portal-page">
      <div className="crew-header">
        <p className="eyebrow">Training Track</p>
        <h1>{meta.title}</h1>
        <p className="lead">{meta.description}</p>
      </div>

      {hasTeams && (
        <TeamTabs active={activeTeam} onChange={setActiveTeam} />
      )}

      {team && (
        <RoleFocusChips team={team} activeRole={activeRole} onRoleChange={setActiveRole} />
      )}

      {loading && <p>Loading…</p>}
      {error && <p className="error">Error: {error}</p>}

      {!loading && !error && (
        <PhaseTimeline
          phases={phases}
          schedule={schedule}
          modules={modules}
          crew={crew}
          team={activeTeam}
          activeRole={activeRole}
          completedIds={completedIds}
        />
      )}
    </div>
  )
}
